import { cardFullscreen } from "../../../shared/cards/CardFullscreen";
import { createVerseCardFace } from "../../../shared/cards/cardFace";
import { keywordsForVerse } from "../game/cards/keywords";
import type { CardInstance } from "../game/cards/types";

export class RevealModal {
  private root = document.getElementById("reveal-modal")!;
  private cardMount = document.getElementById("reveal-card-mount")!;
  private refEl = document.getElementById("reveal-ref")!;
  private metaEl = document.getElementById("reveal-category")!;
  private textEl = document.getElementById("reveal-text")!;
  private keywordsEl = document.getElementById("reveal-keywords")!;
  private summaryEl = document.getElementById("reveal-summary")!;
  private activateBtn = document.getElementById("btn-reveal-activate") as HTMLButtonElement;
  private closeBtn = document.getElementById("btn-reveal-close")!;
  private currentId: string | null = null;

  constructor(
    private readonly handlers: {
      onClose: () => void;
    },
  ) {
    this.closeBtn.addEventListener("click", () => this.handlers.onClose());
    // No click-to-auto-play — player must drag the card onto the board
    this.activateBtn.textContent = "Drag from hand";
    this.activateBtn.disabled = true;
    this.activateBtn.title = "Close this and drag the card onto a lie or slot";
    this.activateBtn.addEventListener("click", () => this.handlers.onClose());
    this.root.addEventListener("click", (e) => {
      if (e.target === this.root) this.handlers.onClose();
    });
  }

  isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  getCurrentId(): string | null {
    return this.currentId;
  }

  show(card: CardInstance, _canProclaim?: boolean): void {
    this.currentId = card.instanceId;
    this.refEl.textContent = card.def.reference;
    const speak = card.def.speakSeconds ?? 2.5;
    this.metaEl.textContent = `${card.def.category} · ${card.def.kind} · ${speak.toFixed(1)}s speak`;
    this.textEl.textContent = card.def.textKjv;
    const proclaim =
      card.def.proclaimText && card.def.proclaimText !== card.def.textKjv
        ? `Proclaim: “${card.def.proclaimText}”\n\n${card.def.effectSummary}\n\nDrag this card from your hand onto the board to play.`
        : `${card.def.effectSummary}\n\nDrag this card from your hand onto the board to play.`;
    this.summaryEl.textContent = proclaim;
    this.renderKeywords(card);
    this.renderCardFace(card);
    this.activateBtn.disabled = false; // closes modal — not auto-play
    this.activateBtn.textContent = "Close · drag to play";
    this.root.classList.remove("hidden");
  }

  private renderCardFace(card: CardInstance): void {
    const kws = keywordsForVerse(card.def);
    this.cardMount.replaceChildren();
    const face = createVerseCardFace({
      verseId: card.def.id,
      reference: card.def.reference,
      category: card.def.category,
      speakSeconds: card.def.speakSeconds ?? 2.5,
      metaLine: `${card.def.category} · ${card.def.kind}`,
      keywordLabels: kws.map((k) => k.label),
      size: "reveal",
    });
    face.classList.add("card-zoomable");
    face.title = "Click for full screen";
    face.addEventListener("click", () => {
      const speak = card.def.speakSeconds ?? 2.5;
      cardFullscreen.show({
        verseId: card.def.id,
        reference: card.def.reference,
        category: card.def.category,
        speakSeconds: speak,
        metaLine: `${card.def.category} · ${card.def.kind}`,
        keywords: kws.map((k) => ({
          id: k.id,
          label: k.label,
          reminder: k.reminder,
        })),
        textKjv: card.def.textKjv,
        proclaimText: card.def.proclaimText,
        effectSummary: card.def.effectSummary,
        detailRows: [
          { label: "Speak", value: `${speak.toFixed(1)}s` },
          { label: "Kind", value: card.def.kind },
          { label: "Category", value: card.def.category },
        ],
        studyLayout: true,
      });
    });
    this.cardMount.appendChild(face);
  }

  private renderKeywords(card: CardInstance): void {
    this.keywordsEl.replaceChildren();
    const kws = keywordsForVerse(card.def);
    for (const k of kws) {
      const chip = document.createElement("span");
      chip.className = `keyword-chip kw-${k.id}`;
      chip.textContent = k.label;
      chip.title = k.reminder;
      chip.setAttribute("aria-label", `${k.label}. ${k.reminder}`);
      this.keywordsEl.appendChild(chip);
    }
  }

  refresh(card: CardInstance | null, _canProclaim?: boolean): void {
    if (!card || !this.isOpen()) return;
    // Only rebuild face when the revealed card instance changes — syncUi
    // used to call this every ~100ms and re-request missing art forever.
    if (this.currentId === card.instanceId && this.cardMount.childElementCount > 0) {
      return;
    }
    this.currentId = card.instanceId;
    this.renderCardFace(card);
    this.renderKeywords(card);
  }

  hide(): void {
    this.currentId = null;
    this.cardMount.replaceChildren();
    this.root.classList.add("hidden");
  }
}
